import type { BrokerQuoteView } from "./historyReader.js";

/** build.ts(Node)・client.ts(ブラウザ)の両方から呼ぶ純粋な文字列生成関数のみを置く */

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type SortColumn = "buy" | "sell";
export type SortOrder = "asc" | "desc";
export interface SortState {
  column: SortColumn;
  order: SortOrder;
}

/**
 * swapCurrencyがJPY以外(楽天FX・OANDA Japanの非クロス円ペア等、quote通貨建ての生値)かどうか。
 * 円建てではなく他業者の値と単位が異なるため単純比較できない(06-decisions.md
 * 「OANDA Japan・楽天FXの非クロス円ペア復元」参照)。
 */
function isNonJpyQuote(quote: BrokerQuoteView): boolean {
  return quote.swapCurrency !== "JPY";
}

function sortByColumn(
  quotes: BrokerQuoteView[],
  sort: SortState,
): BrokerQuoteView[] {
  const key = sort.column === "buy" ? "buySwap" : "sellSwap";
  return [...quotes].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // 値なし(公表前等)は常に末尾
    if (bv === null) return -1;
    return sort.order === "asc" ? av - bv : bv - av;
  });
}

/**
 * 買・売のうちプラス側(値が大きい方)を「実質的に受け取れるスワップ額」とみなす。
 * 通常は買・売のどちらか一方がプラス、もう一方がマイナスの鏡写しになるため、
 * 大きい方を採用すれば自然と「プラスの方の額」になる。値なし(null)は最下部に沈める。
 */
function bestSwapValue(quote: BrokerQuoteView): number {
  const { buySwap, sellSwap } = quote;
  if (buySwap === null && sellSwap === null) return -Infinity;
  if (buySwap === null) return sellSwap as number;
  if (sellSwap === null) return buySwap;
  return Math.max(buySwap, sellSwap);
}

function sortByBestSwap(quotes: BrokerQuoteView[]): BrokerQuoteView[] {
  return [...quotes].sort((a, b) => bestSwapValue(b) - bestSwapValue(a));
}

/**
 * 円建て(JPY)の行は`sort`に従って通常通り値でソートし、非円建ての行は値によるソート対象から除外して
 * 常に末尾に固定する(元の相対順序は維持する)。他社と単位が異なる値を同じ基準で並べ替えると
 * 誤解を招くため(06-decisions.md「OANDA Japan・楽天FXの非クロス円ペア復元」参照)。
 * `sort`未指定時(デフォルト表示)は、買・売のプラス側が大きい順(bestSwapValue降順)を初期並び順とする。
 */
function sortQuotes(
  quotes: BrokerQuoteView[],
  sort: SortState | null,
): BrokerQuoteView[] {
  const jpy = quotes.filter((q) => !isNonJpyQuote(q));
  const nonJpy = quotes.filter(isNonJpyQuote);
  const sortedJpy = sort ? sortByColumn(jpy, sort) : sortByBestSwap(jpy);
  return [...sortedJpy, ...nonJpy];
}

function sortButton(
  column: SortColumn,
  label: string,
  sort: SortState | null,
): string {
  const active = sort?.column === column;
  const order = active ? sort.order : null;
  const icon = order === "asc" ? "▲" : order === "desc" ? "▼" : "⇅";
  const iconClass = active ? "sort-icon sort-icon-active" : "sort-icon";
  const ariaSort =
    order === "asc" ? "ascending" : order === "desc" ? "descending" : "none";
  return `<th aria-sort="${ariaSort}"><button type="button" class="sort-button" data-sort-column="${column}">${label} <span class="${iconClass}">${icon}</span></button></th>`;
}

export function formatSwap(value: number | null): string {
  return value === null ? "―" : value.toLocaleString("ja-JP");
}

/**
 * 買スワップ・売スワップそれぞれの最大値を求める(値なしは対象外)。同値が複数あれば全て該当させる。
 * 非円建ての行は単位が異なり単純比較できないため対象外とする(円建て同士でのみ「最良値」を決める)。
 */
function findExtremes(quotes: BrokerQuoteView[]): {
  maxBuy: number | null;
  maxSell: number | null;
} {
  let maxBuy: number | null = null;
  let maxSell: number | null = null;
  for (const q of quotes) {
    if (isNonJpyQuote(q)) continue;
    if (q.buySwap !== null && (maxBuy === null || q.buySwap > maxBuy)) {
      maxBuy = q.buySwap;
    }
    if (q.sellSwap !== null && (maxSell === null || q.sellSwap > maxSell)) {
      maxSell = q.sellSwap;
    }
  }
  return { maxBuy, maxSell };
}

export function renderTable(
  quotes: BrokerQuoteView[],
  sort: SortState | null = null,
): string {
  if (quotes.length === 0) {
    return `<p class="empty-state">この日付のデータはありません。</p>`;
  }

  const { maxBuy, maxSell } = findExtremes(quotes);
  const sortedQuotes = sortQuotes(quotes, sort);

  const rows = sortedQuotes
    .map((q, index) => {
      const nonJpy = isNonJpyQuote(q);
      // 円建て→非円建てへの切り替わり位置に区切り行を1つ挟む(非円建ての値は他社と単位が異なり
      // 単純比較できないことを明示するため)
      const divider =
        nonJpy &&
        (index === 0 ||
          !isNonJpyQuote(sortedQuotes[index - 1] as BrokerQuoteView))
          ? `<tr class="currency-divider"><td colspan="4">ここから下はスワップ額が円建て以外のペア(参考値、ソート対象外)です</td></tr>`
          : "";

      const daysText = q.swapDays !== null ? `${q.swapDays}日分` : "―";
      const brokerLabel = q.brokerSwapPageUrl
        ? `<a href="${escapeHtml(q.brokerSwapPageUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(q.brokerDisplayName)}</a>`
        : escapeHtml(q.brokerDisplayName);
      const buyClass =
        q.buySwap !== null && q.buySwap === maxBuy
          ? "num-cell swap-best"
          : "num-cell";
      const sellClass =
        q.sellSwap !== null && q.sellSwap === maxSell
          ? "num-cell swap-best"
          : "num-cell";
      const rowClass = nonJpy ? ' class="row-non-jpy"' : "";
      return `${divider}<tr${rowClass}>
        <td class="broker-cell">${brokerLabel}</td>
        <td class="${buyClass}">${formatSwap(q.buySwap)}</td>
        <td class="${sellClass}">${formatSwap(q.sellSwap)}</td>
        <td>${daysText}</td>
      </tr>`;
    })
    .join("");

  return `<div class="table-scroll">
    <table class="swap-table">
      <thead>
        <tr>
          <th>業者</th>
          ${sortButton("buy", "買", sort)}
          ${sortButton("sell", "売", sort)}
          <th>日数</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`;
}
